import { Component, ElementRef, Input, OnInit, Renderer2, ViewChild } from "@angular/core";
import { combineLatest, EMPTY, interval, Observable } from "rxjs";
import { map, startWith } from "rxjs/operators";
import { PaysafeService } from "../../services/paysafe.service";
import { PaysafeField } from "../../models/paysafe-field";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { ButtonTextKeys } from "@modules/paysafe/models/button-text.type";
import { PaysafeCreditCardNumberField } from "@modules/paysafe/models/paysafe-credit-card-number-field";
import {
	PAYSAFE_CC_NAME_VALIDATION_REGEX_KEYPRESS,
	PAYSAFE_CC_NAME_VALIDATION_REGEX,
	PAYSAFE_CC_NAME_VALIDATION_MIN_LENGTH,
	PAYSAFE_CC_NAME_VALIDATION_MAX_LENGTH,
	PAYSAFE_CC_NUMBER_DEFAULT_ERROR,
	PAYSAFE_CC_NUMBER_DEFAULT_ALLOWED_BRANDS,
	PAYSAFE_CC_NUMBER_MC_AVAILABLE_ALLOWED_BRANDS,
	PAYSAFE_CC_NUMBER_MC_AVAILABLE_ERROR
} from "@modules/paysafe/models/paysafe-config";
import { PaysafeResponseDetails } from "@modules/paysafe/models/paysafe-response-details";
import { CreditCardDeposit } from "@modules/paysafe/models/credit-card-deposit";
import { MOBILE_AGENTS } from "@helpers/constants";
import { CheckoutDepositData } from "@modules/checkout/entities/checkout.deposit-data";

@Component({
	selector: "app-paysafe",
	templateUrl: "./paysafe.component.html",
	styleUrls: ["./paysafe.component.scss"],
	standalone: false
})
export class PaysafeComponent implements OnInit {
	public ccNamePasteErrorActive: boolean = false;
	public cardHolderNameField: UntypedFormControl = new UntypedFormControl("", [
		Validators.required,
		Validators.minLength(PAYSAFE_CC_NAME_VALIDATION_MIN_LENGTH),
		Validators.maxLength(PAYSAFE_CC_NAME_VALIDATION_MAX_LENGTH),
		Validators.pattern(PAYSAFE_CC_NAME_VALIDATION_REGEX)
	]);
	public nameForm: UntypedFormGroup = new UntypedFormGroup({
		cardHolder: this.cardHolderNameField
	});
	public cardNumberField: PaysafeCreditCardNumberField = null;
	public expiryDateField = new PaysafeField("expiryDate", "paysafe_expiry_date_error");
	public cvvField = new PaysafeField("cvv", "paysafe_cvv_error");
	@ViewChild("payNow", { static: false })
	public payButtonElement: ElementRef<HTMLButtonElement> = null;
	public buttonText$: Observable<ButtonTextKeys> = EMPTY;
	public errors$: Observable<string[]> = EMPTY;
	public buttonDisabled: Observable<boolean> = EMPTY;
	private paysafeInstance = null;
	private cardHolderNameFieldValid: Observable<boolean> = this.cardHolderNameField.statusChanges.pipe(
		startWith("INVALID"),
		map((value: "VALID" | "INVALID") => value === "VALID")
	);
	private formValid: Observable<boolean> = EMPTY;
	public isMobile = MOBILE_AGENTS.test(window.navigator.userAgent);
	public creditCardsEnabled = false;
	@Input() isCheckout: boolean = false;
	@Input() checkoutDepositData: CheckoutDepositData = null;

	public constructor(
		private renderer: Renderer2,
		private service: PaysafeService
	) {}

	public get payButton(): HTMLButtonElement {
		return this.payButtonElement.nativeElement;
	}

	public ngOnInit(): void {
		this.service.userIsFromUK().then((isUK: boolean) => {
			const urlCreditCardsEnabled =
				new URLSearchParams(window.location.search).get("credit_cards_enabled") === "true";
			this.creditCardsEnabled = isUK || urlCreditCardsEnabled;
		});

		this.cardNumberField = new PaysafeCreditCardNumberField(
			"cardNumber",
			PAYSAFE_CC_NUMBER_DEFAULT_ERROR,
			"paysafe_cc_number_secondary_error",
			PAYSAFE_CC_NUMBER_DEFAULT_ALLOWED_BRANDS
		);

		this.formValid = combineLatest([
			this.cardNumberField.isValid$,
			this.expiryDateField.isValid$,
			this.cvvField.isValid$,
			this.cardHolderNameFieldValid
		]).pipe(map((statuses: boolean[]) => !statuses.includes(false)));

		this.buttonDisabled = combineLatest([this.service.disableButton, this.formValid]).pipe(
			map((statuses) => statuses.includes(false))
		);

		this.service.importScript().then(({ instance, response }) => {
			this.paysafeInstance = instance;
			this.handleInit(instance, response);
		});
		this.buttonText$ = this.service.buttonText$;
		this.errors$ = this.service.errors$;
	}

	public handleCardHolderChange = (): void => this.service.resetErrors();

	public handleCardHolderKeypress = ($event: KeyboardEvent): boolean => {
		this.ccNamePasteErrorActive = false;
		return RegExp(PAYSAFE_CC_NAME_VALIDATION_REGEX_KEYPRESS).test($event.key);
	};

	public handleCardHolderPaste = ($event: ClipboardEvent): boolean => {
		const clipboardData: string = $event.clipboardData.getData("text/plain");
		this.ccNamePasteErrorActive = !RegExp(PAYSAFE_CC_NAME_VALIDATION_REGEX).test(clipboardData);
		return RegExp(PAYSAFE_CC_NAME_VALIDATION_REGEX).test(clipboardData);
	};

	private handleInit(instance, response: CreditCardDeposit<PaysafeResponseDetails>): void {
		const allowedMasterCardFlag: boolean = response.options.allowed_deposit_mastercard;
		if (allowedMasterCardFlag) {
			this.updateCCNumberField();
		}

		this.initField(this.cardNumberField);
		this.initField(this.expiryDateField);
		this.initField(this.cvvField);

		this.renderer.listen(this.payButton, "click", this.submit(instance));
	}

	private submit =
		(instance) =>
		($event): void => {
			this.enlargeIframe();
			this.service.runTokenization(
				this.cardHolderNameField.value,
				instance,
				this.isCheckout,
				this.checkoutDepositData
			);
		};

	private enlargeIframe(): void {
		let timer = interval(200);
		let sub = timer.subscribe((value) => {
			let iframe: HTMLIFrameElement = document.querySelector("iframe#Cardinal-CCA-IFrame");
			if (iframe) {
				const modalContent: HTMLElement = document.querySelector("#Cardinal-ModalContent");
				modalContent.style.height = "auto";
				iframe.setAttribute("height", window.innerHeight * 0.75 + "");
				sub.unsubscribe();
			}

			if (value >= 30) {
				sub.unsubscribe();
			}
		});
	}

	private initField(field: PaysafeField): void {
		const fieldElement = this.paysafeInstance.fields(field.name);
		field.handleInitiatedField(fieldElement);
	}

	private updateCCNumberField(): void {
		this.cardNumberField.setErrorMsg(PAYSAFE_CC_NUMBER_MC_AVAILABLE_ERROR);
		this.cardNumberField.setSupportedCardBrands(PAYSAFE_CC_NUMBER_MC_AVAILABLE_ALLOWED_BRANDS);
	}
}
